import PhotoSwipe from 'photoswipe';
import PhotoSwipeUIDefault from 'photoswipe/dist/photoswipe-ui-default';
import 'photoswipe/dist/photoswipe.css';
import 'photoswipe/dist/default-skin/default-skin.css';
import './FullscreenGallery.scss';

const PLAYER_INTERVAL_MS = 800;
const FIRST_IMAGE_INDEX = 0;
const MIN_IMAGES_COUNT_TO_SHOW_PLAY_BUTTON = 2;

const PHOTO_SWIPER_BASE_OPTIONS = {
  closeOnScroll: false,
  shareEl: false,
  modal: false,
  arrowEl: false,
  closeEl: false,
  counterEl: false,
  history: false,
  timeToIdle: 0,
  timeToIdleOutside: 0,
  barsSize: {
    top: 0,
    bottom: 0,
  },
};

const CLASSES = {
  visible: '-visible',
  active: '-active',
  progress: '-progress',
  noPlayer: '-no-player',
};

export class FullscreenGallery {
  isPhotoSwipeMounted = false;

  constructor() {
    this.rootNode = document.querySelector('[data-fullscreen-gallery-root]');

    this.photoSwipeNode = this.rootNode.querySelector('[data-photoswipe-root]');
    this.playButtonNode = this.rootNode.querySelector('[data-play-button]');
    this.closeButtonNode = this.rootNode.querySelector('[data-close-button]');
    this.leftArrowNode = this.rootNode.querySelector('[data-arrow-left]');
    this.rightArrowNode = this.rootNode.querySelector('[data-arrow-right]');
    this.progressBarNode = this.rootNode.querySelector('[data-progress-bar]');
    this.dateNode = this.rootNode.querySelector('[data-chip="date"]');
    this.houseNode = this.rootNode.querySelector('[data-chip="house"]');
    this.albumNode = this.rootNode.querySelector('[data-chip="album"]');

    this.interval = null;
    this.isLoadedInterval = null;

    if (window.isMobile) {
      this.fixCloseButtonPressOnMobile();
    }
  }

  // https://github.com/dimsemenov/PhotoSwipe/issues/1608#issuecomment-465266844
  fixCloseButtonPressOnMobile = () => {
    this.rootNode.querySelector('.pswp').addEventListener(
      'pswpTap',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
      },
      true,
    );
  };

  preventSwipeWhenPlayerIsInProgress = () => {
    const preventSwipe = (event) => {
      const isPlayerInProgress = !!this.interval;

      if (isPlayerInProgress) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    this.photoSwipe.container.addEventListener('pointerdown', preventSwipe);
    this.photoSwipe.container.addEventListener('MSPointerDown', preventSwipe);
    this.photoSwipe.container.addEventListener('touchstart', preventSwipe);
    this.photoSwipe.container.addEventListener('mousedown', preventSwipe);
  };

  init = ({ images, house, album, initialIndex }) => {
    this.images = images;
    this.house = house;
    this.album = album;

    this.showFullscreenGallery();

    const photoSwipe = new PhotoSwipe(this.photoSwipeNode, PhotoSwipeUIDefault, images, {
      ...PHOTO_SWIPER_BASE_OPTIONS,
      index: initialIndex,
    });

    this.photoSwipe = photoSwipe;

    photoSwipe.listen('gettingData', this.handlePhotoSwiperGettingData);

    photoSwipe.listen('afterChange', () => {
      this.updateProgressBar();
      this.updateInfo();
    });
    photoSwipe.listen('close', this.handlePhotoSwiperClose);

    photoSwipe.init();

    this.isPhotoSwipeMounted = true;

    this.initPlayButton();
    this.initArrows();
    this.initProgressBar();
    this.initCloseButton();
    this.updateInfo();

    this.preventSwipeWhenPlayerIsInProgress();
  };

  handlePhotoSwiperGettingData = (index, item) => {
    if (!item.w || !item.h) {
      const img = new Image();

      img.onload = () => {
        /**
         * Problem: if the image finished loading after the user already closed the gallery,
         * calling "photoSwipe.updateSize" throws.
         * Solution: "isPhotoSwipeMounted" flags whether the gallery is still open —
         * if it's closed, do nothing.
         */
        if (!this.isPhotoSwipeMounted) {
          return;
        }

        item.w = img.width;
        item.h = img.height;

        /**
         * Problem: if the image isn't within PhotoSwipe's current render range yet
         * (item.initialPosition not set by the library yet), forcing
         * updateSize/invalidateCurrItems throws during zoom/pan recalculation.
         * Solution: only force a resize for the currently shown index — for the rest,
         * caching w/h is enough for when they're shown later.
         */
        if (index !== this.photoSwipe.getCurrentIndex()) {
          return;
        }

        this.photoSwipe.invalidateCurrItems();
        this.photoSwipe.updateSize(true);
      };

      img.src = item.src;
    }
  };

  goToImage = (imageIndex) => {
    if (!this.isPhotoSwipeMounted) return;

    this.photoSwipe.goTo(imageIndex);

    if (this.interval && !this.photoSwipe.currItem.loaded) {
      clearInterval(this.interval);
      this.interval = null;
      this.isLoadedInterval = setInterval(() => {
        if (this.photoSwipe.currItem.loaded) {
          clearInterval(this.isLoadedInterval);
          this.isLoadedInterval = null;
          this.interval = setInterval(this.playerIntervalHandler, PLAYER_INTERVAL_MS);
        }
      }, PLAYER_INTERVAL_MS / 8);
    }

    this.updateProgressBar();
    this.updateInfo();
  };

  updateInfo = () => {
    const currentIndex = this.photoSwipe.getCurrentIndex();
    const currentImage = this.images[currentIndex];

    this.dateNode.textContent = currentImage.date;
    this.houseNode.textContent = this.house;
    this.albumNode.textContent = this.album;
  };

  initPlayButton = () => {
    if (this.images.length < MIN_IMAGES_COUNT_TO_SHOW_PLAY_BUTTON) {
      this.rootNode.classList.add(CLASSES.noPlayer);
    } else {
      this.rootNode.classList.remove(CLASSES.noPlayer);
    }

    this.playButtonNode.addEventListener('click', this.handlePlayButtonClick);
  };

  showFullscreenGallery = () => {
    this.rootNode.classList.add(CLASSES.visible);
  };

  hideFullscreenGallery = () => {
    this.rootNode.classList.remove(CLASSES.visible);
  };

  handlePlayButtonClick = () => {
    if (!this.interval) {
      this.startPlayer();
    } else {
      this.stopPlayer();
    }
  };

  playerIntervalHandler = () => {
    const currentIndex = this.photoSwipe.getCurrentIndex();

    if (currentIndex < this.images.length - 1) {
      this.goToImage(currentIndex + 1);
    } else {
      this.stopPlayer();
    }
  };

  startPlayer = () => {
    this.rootNode.classList.add(CLASSES.progress);

    this.goToImage(FIRST_IMAGE_INDEX);

    this.interval = setInterval(this.playerIntervalHandler, PLAYER_INTERVAL_MS);

    this.playButtonNode.classList.add(CLASSES.active);
  };

  stopPlayer = () => {
    clearInterval(this.interval);
    clearInterval(this.isLoadedInterval);
    this.interval = null;
    this.isLoadedInterval = null;
    this.playButtonNode.classList.remove(CLASSES.active);
    this.rootNode.classList.remove(CLASSES.progress);
  };

  handlePhotoSwiperClose = () => {
    this.isPhotoSwipeMounted = false;

    this.hideFullscreenGallery();

    this.playButtonNode.removeEventListener('click', this.handlePlayButtonClick);
    this.leftArrowNode.removeEventListener('click', this.handleLeftArrowClick);
    this.rightArrowNode.removeEventListener('click', this.handleRightArrowClick);
    this.closeButtonNode.removeEventListener('click', this.handleCloseButtonClick);

    this.progressBarNode.innerHTML = '';

    clearInterval(this.interval);
    clearInterval(this.isLoadedInterval);
    this.interval = null;
    this.isLoadedInterval = null;
  };

  initArrows = () => {
    this.leftArrowNode.addEventListener('click', this.handleLeftArrowClick);
    this.rightArrowNode.addEventListener('click', this.handleRightArrowClick);

    this.updateProgressBar();
  };

  initProgressBar = () => {
    const imagesCount = this.images.length;
    const currentIndex = this.photoSwipe.getCurrentIndex();
    const progressNodes = Array.from(Array(imagesCount));

    progressNodes.forEach((node, i) => {
      const div = document.createElement('div');
      if (currentIndex === i) {
        div.classList.add(CLASSES.active);
      }

      this.progressBarNode.appendChild(div);
    });
  };

  handleLeftArrowClick = () => {
    const currentIndex = this.photoSwipe.getCurrentIndex();
    const imagesCount = this.images.length;
    const nextImageIndex = currentIndex > 0 ? currentIndex - 1 : imagesCount - 1;

    this.goToImage(nextImageIndex);
  };

  handleRightArrowClick = () => {
    const currentIndex = this.photoSwipe.getCurrentIndex();
    const imagesCount = this.images.length;
    const nextImageIndex = currentIndex < imagesCount - 1 ? currentIndex + 1 : FIRST_IMAGE_INDEX;

    this.goToImage(nextImageIndex);
  };

  handleCloseButtonClick = () => {
    this.photoSwipe.close()
  }

  initCloseButton = () => {
    this.closeButtonNode.addEventListener('click', this.handleCloseButtonClick);
  };

  updateProgressBar = () => {
    const currentIndex = this.photoSwipe.getCurrentIndex();
    const progressNodes = this.progressBarNode.querySelectorAll('div');

    progressNodes.forEach((node, i) => {
      if (i === currentIndex) {
        node.classList.add(CLASSES.active);
      } else {
        node.classList.remove(CLASSES.active);
      }
    });
  };
}
